import React, { useState } from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "@/components/layout/MarketingHeader";
import MarketingFooter from "@/components/layout/MarketingFooter";
import { ArrowUpRight, ArrowRight, Plus, Minus } from "lucide-react";
import { openVipModal } from "@/lib/vipModal";
import { optimizeImage } from "@/lib/img";

const STORY_IMG = "https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/e7upxv07_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2008_46_21%20PM.png";

const FAQS = [
  {
    q: "What is LocalizeHER?",
    a: "A marketing membership, business directory, and community built specifically for women-owned local businesses — designed to help you get seen, supported, and equipped to become a category leader in your city.",
  },
  {
    q: "When does the membership officially open?",
    a: "We're currently building out our founding cohort. By locking in your VIP spot now, you'll be among the first invited the moment doors open — and you'll get the founding-member rate locked in for life.",
  },
  {
    q: "What's included in the membership?",
    a: "Premium HER Guide directory placement, a growing library of marketing templates and AI prompts, monthly workshops, Slack community access, in-person events, and discounted coaching with Brea. Accelerate members also get business hot seats, marketing audits, and priority support.",
  },
  {
    q: "Do I need to be in Phoenix to join?",
    a: "Not anymore. We're launching online with Phoenix as our home base and expanding city by city. Reserve your spot and you'll be part of the wave that brings LocalizeHER to your community.",
  },
  {
    q: "Is the membership only for businesses, or can solopreneurs join?",
    a: "If you're a woman running a local business — solo or with a team, brick-and-mortar or service-based — LocalizeHER is built for you. We're creating a supportive, inclusive community where women entrepreneurs can learn, grow, and build meaningful connections. LocalizeHER proudly welcomes LGBTQ+ members and allies and is committed to fostering a judgment-free space where every founder feels seen, heard, respected, and empowered to succeed.",
  },
  {
    q: "Can I cancel at any time?",
    a: "Yes. There are no annual contracts. Once we open the doors, you can cancel any time before your next billing cycle and keep access through the end of the period.",
  },
  {
    q: "How is this different from networking groups or chambers of commerce?",
    a: "We're not built around dinners and badges. Every part of LocalizeHER is engineered around one outcome — making your business the name everyone in your city already knows. Visibility, strategy, education, and community — operator-grade, not surface-level.",
  },
  {
    q: "How do I get featured in the HER Guide?",
    a: "Every paid member gets a listing in the HER Guide directory. Accelerate members get featured placement, badges, and editorial spotlights in our journal and social channels.",
  },
];

function FaqItem({ item, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border-b border-[var(--lh-border)] last:border-b-0"
      data-testid={`faq-item-${index}`}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-6 py-6 text-left group"
        aria-expanded={open}
        data-testid={`faq-trigger-${index}`}
      >
        <span className="font-display-bold text-xl md:text-2xl tracking-tighter leading-[1.15] flex-1">
          {item.q}
        </span>
        <span
          className="shrink-0 mt-1 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 group-hover:bg-[var(--lh-peach)] group-hover:text-[var(--lh-espresso-noir)]"
          style={{
            background: open ? "var(--lh-peach)" : "transparent",
            color: open ? "var(--lh-espresso-noir)" : "var(--lh-cognac)",
            border: "1px solid var(--lh-cognac)",
          }}
          aria-hidden="true"
        >
          {open ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      </button>
      {open && (
        <div
          className="pb-6 pr-12 text-base lg:text-lg font-medium leading-relaxed"
          data-testid={`faq-answer-${index}`}
        >
          {item.a}
        </div>
      )}
    </div>
  );
}

const VALUES = [
  { n: "01", title: "Visibility is leverage", body: "We don't believe in waiting to be discovered. We engineer for being found." },
  { n: "02", title: "Local is the new global", body: "Category leaders are built one zip code at a time. We obsess over the local." },
  { n: "03", title: "Community over networking", body: "Networking is small talk in a hotel ballroom. Community is the group chat that builds your business." },
  { n: "04", title: "Real talk, real work", body: "No fluff. No fake hustle. Just the actual operators doing the actual work, sharing what works." },
];

const PILLARS = [
  { title: "Marketing", body: "Tactical templates, playbooks, AI prompts, brand strategy, and senior operators on call." },
  { title: "Visibility", body: "The HER Guide, our directory of vetted women-owned businesses, surfaced where customers search." },
  { title: "Community", body: "A private channel network for wins, brand-clinic feedback, and local collabs." },
  { title: "Coaching", body: "Fractional CMO advisory, business strategy, and brand audits at member rates." },
];

export default function About() {
  return (
    <div className="min-h-screen" data-testid="about-page">
      <MarketingHeader />

      {/* Editorial hero */}
      <section className="pt-28 lg:pt-32 pb-16 lg:pb-20 section-light" style={{ borderBottom: "1px solid var(--lh-border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-6" style={{ color: "var(--lh-cognac)" }}>About LocalizeHER</div>
            <h1
              className="font-display-bold text-5xl md:text-7xl lg:text-[5.4rem] leading-[0.95] tracking-tighter"
              data-testid="about-headline"
            >
              Networking is cute. <em>Revenue is hotter.</em>
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pt-12">
            <p className="text-lg lg:text-xl font-medium leading-relaxed">
              Marketing education, growth tools, and a community built to help women-owned businesses become the go-to brands in their cities.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="pt-16 pb-8 lg:pt-20 lg:pb-10 section-light" data-testid="about-story-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <img
              src={optimizeImage(STORY_IMG, { w: 900, name: "localizeher-founder-portrait" })}
              alt="Brea, founder of LocalizeHER"
              className="w-full h-auto object-contain rounded-2xl"
              loading="lazy"
              decoding="async"
              data-testid="founder-photo"
            />
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <div className="overline mb-6 text-[var(--lh-caramel)]">The Origin</div>
            <h2 className="font-display-bold text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              Meet Brea. <em>Phoenix-based founder, market-builder.</em>
            </h2>
            <div className="mt-8 space-y-5 text-base lg:text-lg leading-relaxed font-medium">
              <p>
                After more than 20 years helping franchise brands and multi-unit businesses grow,
                I noticed something missing: meaningful support for local women-owned businesses.
              </p>
              <p>
                I&rsquo;ve spent my career as a marketing executive, operator, entrepreneur, and
                growth strategist. I&rsquo;ve helped scale brands, led teams, embraced emerging
                technology, and built businesses myself. Along the way, I developed a passion for
                innovation, AI, and the tools that help businesses work smarter, grow faster, and
                compete at a higher level.
              </p>
              <p className="font-semibold">
                What I learned is that access changes everything.
              </p>
              <p>
                Yet too many women business owners are still trying to figure it out alone. Too
                many communities are built around networking, not growth. Too many women are
                working tirelessly to build incredible businesses without the guidance,
                opportunities, and support systems often found in the boys&rsquo; club.
              </p>
              <p className="font-semibold">
                That&rsquo;s what inspired me to create LocalizeHER.
              </p>
              <p>
                Because when women-owned businesses thrive, entire communities thrive with them.
              </p>
              <p
                className="pt-2 font-display-bold tracking-tighter"
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontSize: "1.75rem",
                  lineHeight: 1.15,
                  color: "var(--lh-cognac)",
                }}
              >
                A seat at the table. A voice in the room. A community in your corner.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What we believe */}
      <section className="pt-8 pb-16 lg:pt-10 lg:pb-20 section-light" data-testid="about-values-section" style={{ borderTop: "1px solid var(--lh-border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <h2 className="font-display-bold text-5xl md:text-7xl tracking-tighter leading-[0.9] max-w-3xl">
            What we <em>believe.</em>
          </h2>
          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {VALUES.map((v) => (
              <div
                key={v.n}
                className="p-10 rounded-2xl"
                style={{
                  background: "#FFFFFF",
                  border: "1px solid var(--lh-border)",
                  boxShadow: "0 1px 0 rgba(31,26,21,0.02), 0 18px 36px -28px rgba(31,26,21,0.12)",
                }}
                data-testid={`value-card-${v.n}`}
              >
                <div className="font-display-bold text-5xl mb-6" style={{ color: "var(--lh-cognac)" }}>{v.n}</div>
                <h3 className="font-display-bold text-3xl mb-3">{v.title}</h3>
                <p className="text-base font-medium leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        className="py-16 lg:py-20 section-light"
        style={{ borderTop: "1px solid var(--lh-border)" }}
        data-testid="about-faq-section"
      >
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="overline mb-4" style={{ color: "var(--lh-cognac)" }}>
                Frequently Asked
              </div>
              <h2 className="font-display-bold text-5xl md:text-6xl tracking-tighter leading-[0.95]">
                Real questions. <em>Real answers.</em>
              </h2>
              <p className="mt-6 text-base font-medium max-w-md">
                Everything you wanted to know about LocalizeHER before reserving your VIP spot.
                Don&apos;t see your question? Reach out at{" "}
                <a
                  href="mailto:hello@localizeher.com"
                  className="font-semibold"
                  style={{ color: "var(--lh-cognac)" }}
                >
                  hello@localizeher.com
                </a>
                .
              </p>
            </div>
            <div className="lg:col-span-7" data-testid="about-faq-list">
              {FAQS.map((item, i) => (
                <FaqItem key={item.q} item={item} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="relative py-16 lg:py-20 text-center overflow-hidden"
        data-testid="about-final-cta"
      >
        {/* Background photo */}
        <div
          className="absolute inset-0 z-0 bg-cover"
          style={{
            backgroundImage:
              "url('https://customer-assets.emergentagent.com/job_her-brand-builders/artifacts/6zedvnti_image.png')",
            backgroundPosition: "center top",
          }}
          aria-hidden="true"
        />
        {/* Dark overlay for legibility */}
        <div
          className="absolute inset-0 z-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(31,26,21,0.72) 0%, rgba(31,26,21,0.78) 60%, rgba(31,26,21,0.88) 100%)",
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 max-w-[900px] mx-auto px-6 md:px-10 lg:px-14">
          <h2 className="font-display-bold text-5xl md:text-7xl tracking-tighter leading-[0.95] text-[var(--lh-warm-ivory)]">
            Where local women <em>become local leaders.</em>
          </h2>
          <p className="mt-6 text-lg font-medium" style={{ color: "rgba(234,216,199,0.88)" }}>
            Lock in a founding member rate. Reserve your seat at the long table.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => openVipModal("interested", "about-final-cta")}
              className="btn-mauve"
              data-testid="about-cta-join"
            >
              Reserve Your VIP Spot <ArrowUpRight size={16} />
            </button>
            <Link to="/her-guide" className="btn-ghost-light" data-testid="about-cta-guide">
              Explore The HER Guide <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
